import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import * as PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getPath } from 'utils/router';
import FileUploadeBack from 'components/form/InputFiles/Model/FileUploadeBack';
import {
    Button,
    Row,
    Col,
    SegmentLoader,
    FieldsetControl,
    RadioControl,
} from '@si-armes/dsfr-react';
import { useForm, FormProvider } from 'react-hook-form';
import InputFiles from 'components/Commun/Form/InputFiles';
import { etapePrecedente, setEtapeActive } from 'store/etapes/etapesActions';
import { getArrayFilesToSend } from 'utils/fileFunction';
import { postAPI, reinitialiseAPI } from 'store/apiReducer';
import usagerClientReducer from 'store/usagerReducer';
import {
    API_CREER_TITRE_DETENTION_URL,
    CONTEXT_CREER_TITRE_DETENTION,
} from 'constants/apiUrls.constants';
import RoutesMesDemarches from 'pages/espace-detenteurs/mes-demarches/routes';
import CreerCompteAdresse from 'pages/compte/creer/etape-1/components/adresse/CreerCompteAdresse';
import InfoChampsObligatoire from '../../../../../../components/form/infoObligatoire/InfoChampsObligatoire';
import { STATUTS_METIER_ASSO } from '../../../../../../utils/Statuts.MetierAsso.constant';
import InformationsGeneralesObligatoires from './InformationsGeneralesObligatoires';

const CreerTitreDetentionPersonnePhysiqueEtape2 = ({ defaultValues, loading, type }) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const [afficherAddressSecondaire, setAfficherAddressSecondaire] = useState(false);
    const [isSuccessSubmitAdresseSecondaire, setIsSuccessSubmitAdresseSecondaire] = useState(false);

    const titreAdress = {
        isAdressSecondaire: 'Adresse de la résidence secondaire où sera stockée la seconde arme',
        isAdressLocalPro: 'Adresse du local professionnel où sera stockée la seconde arme',
    };

    // Récupération des informations personnelles pour déterminer le sous-statut
    const { data: informationsPersonnelles } = useSelector(
        (state) => state.monCompte.recupererInformationsPersonnelles,
    );

    const { isLoading: isLoadingRenseignerAdresseSecondaire } = useSelector(
        (state) => state.adresseVerificationBAN?.verifier,
    );

    const sousStatut = informationsPersonnelles?.informationsMetiersAssociations?.sousStatut;
    const codePaysNaissance = informationsPersonnelles?.personnePhysique?.paysNaissance?.code;
    const dateNaissance = informationsPersonnelles?.personnePhysique?.dateNaissance;
    const personnePhysiqueUuid = informationsPersonnelles?.personnePhysique?.uuid;
    const permisChasser = informationsPersonnelles?.licences?.permisChasser;
    const autorisationDetentionArme =
        informationsPersonnelles?.informationsMetiersAssociations?.documents
            ?.autorisationDetentionArme;

    // Déterminer si c'est le cas 1 (risques exceptionnels) ou cas 2 (risques professionnels)
    const isCasRisquesExceptionnels =
        sousStatut === STATUTS_METIER_ASSO.METIER_PHYSIQUE_RISQUES_EXCEPTIONNELS;
    const isCasRisquesProfessionnels =
        sousStatut === STATUTS_METIER_ASSO.METIER_PHYSIQUE_RISQUES_PRO;
    const isLouveterie = sousStatut === STATUTS_METIER_ASSO.METIER_PHYSIQUE_LOUVETERIE;

    const initializedDefaultValues = useMemo(() => {
        const baseValues = defaultValues || {};

        if (isLouveterie && permisChasser) {
            const fichier = new FileUploadeBack({
                filename: permisChasser.permisChasserFichier?.filename,
                uuid: permisChasser.permisChasserFichier?.uuid,
            });

            return {
                ...baseValues,
                pieceJointes: {
                    ...baseValues.pieceJointes,
                    permisDeChasse: {
                        fichiers: [fichier],
                    },
                },
            };
        }

        if (isCasRisquesExceptionnels && autorisationDetentionArme) {
            const fichier = new FileUploadeBack({
                filename: autorisationDetentionArme?.filename,
                uuid: autorisationDetentionArme?.uuid,
            });

            return {
                ...baseValues,
                pieceJointes: {
                    ...baseValues.pieceJointes,
                    autorisationDetentionArme: {
                        fichiers: [fichier],
                    },
                },
            };
        }

        return baseValues;
    }, [defaultValues, permisChasser]);

    const hookForm = useForm({
        mode: 'onChange',
        defaultValues: initializedDefaultValues,
    });

    const { register, handleSubmit, getValues, watch, formState } = hookForm;
    const { isValid } = formState;

    const {
        data: titreDetention,
        erreur: erreurCreation,
        isLoading: isLoadingTitreDetention,
    } = useSelector((state) => state.titreDetention.creer);

    // Vérifier si la personne est majeure (18 ans ou plus)
    const isMajeure = useMemo(() => {
        if (!dateNaissance) return false;

        const today = new Date();
        const birthDate = new Date(dateNaissance);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        // Vérifier si l'anniversaire a déjà eu lieu cette année
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            return age - 1 >= 18;
        }

        return age >= 18;
    }, [dateNaissance]);

    // Enregistrer les données
    const enregistrer = (data) => {
        // Envoyer les données pour enregistrement

        dispatch(
            postAPI(
                usagerClientReducer,
                API_CREER_TITRE_DETENTION_URL.replace(':type', type),
                data,
                CONTEXT_CREER_TITRE_DETENTION,
            ),
        );
    };

    // redirigé ver la page mes démarches
    if (erreurCreation === false && titreDetention) {
        // initialiser l'etapes & le store creer titre de detention
        dispatch(setEtapeActive(1));
        dispatch(reinitialiseAPI(CONTEXT_CREER_TITRE_DETENTION));
        // Redirection
        history.push(getPath(RoutesMesDemarches));
    }

    const onSubmit = async (dataForm) => {
        // Vérifier si l'adresse a été soumise avec succès
        if (!isSuccessSubmitAdresseSecondaire && afficherAddressSecondaire) {
            return;
        }

        const pieceJointes = await Promise.all(
            Object.entries(dataForm.pieceJointes)
                .filter(([, pieceJointe]) => pieceJointe?.fichiers)
                .map(async ([pieceJointeType, pieceJointe]) => {
                    const fichiers = await getArrayFilesToSend(pieceJointe.fichiers);
                    return {
                        type: pieceJointeType,
                        fichiers,
                    };
                }),
        );

        // Regrouper les champs d'adresse dans un objet adresseSecondaire
        const adresseFields = [
            'numeroDeVoie',
            'extension',
            'typeEtNomDeVoie',
            'lieuDit',
            'codePostal',
            'ville',
        ];
        const adresseSecondaire = {};
        const dataFormCopy = { ...dataForm };

        adresseFields.forEach((field) => {
            if (Object.prototype.hasOwnProperty.call(dataFormCopy, field)) {
                adresseSecondaire[field] = dataFormCopy[field];
                delete dataFormCopy[field];
            }
        });

        const formattedData = {
            ...dataFormCopy,
            pieceJointes,
            adresseSecondaire,
        };

        const hasCertificatMedicalPsychiatrique = formattedData?.pieceJointes?.find((fichier) => {
            return fichier?.type === 'certificatMedicalPsychiatrique';
        });

        if (hasCertificatMedicalPsychiatrique) {
            formattedData.jamaisEteEnTraitementPsychiatrique =
                !formattedData?.suiviTraitementPsychiatrique;
        }

        enregistrer(formattedData);
    };

    useEffect(() => {
        if (isSuccessSubmitAdresseSecondaire) {
            onSubmit(getValues());
        }
    }, [isSuccessSubmitAdresseSecondaire]);

    return (
        <SegmentLoader isLoading={isLoadingRenseignerAdresseSecondaire || isLoadingTitreDetention}>
            <FormProvider {...hookForm}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Row gutters>
                        <Col lg={12}>
                            <h3 className="margin-top-xs">
                                Informations complémentaires à renseigner
                            </h3>
                        </Col>
                    </Row>

                    <Row>
                        <Col col={12}>
                            <InfoChampsObligatoire />
                        </Col>
                    </Row>

                    <InformationsGeneralesObligatoires />

                    {/* Permis de chasse (pour la louveterie) */}
                    {isLouveterie && (
                        <Row gutters className="margin-top-md">
                            <Col lg={12}>
                                <div className="margin-bottom-md">
                                    <InputFiles
                                        label="Permis de chasse"
                                        name="pieceJointes.permisDeChasse.fichiers"
                                        error={formState.errors?.pieceJointes?.permisDeChasse}
                                        required
                                    />
                                    <span className="fr-hint-text">
                                        Veuillez importer votre permis de chasse valide
                                    </span>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Cas 1: Personnes exposées à des risques exceptionnels */}
                    {isCasRisquesExceptionnels && (
                        <>
                            <Row gutters>
                                <Col lg={12}>
                                    <div className="margin-bottom-md">
                                        <InputFiles
                                            label="Justification de participation à des séances de tir"
                                            {...register(
                                                'pieceJointes.justificationSeancesTir.fichiers',
                                                {
                                                    required: 'Ce champ est obligatoire',
                                                },
                                            )}
                                            error={
                                                formState.errors?.pieceJointes
                                                    ?.justificationSeancesTir
                                            }
                                            required
                                        />
                                        <span className="fr-hint-text">
                                            Veuillez fournir une justification de participation à
                                            trois séances contrôlées de tir, espacées d'au moins
                                            deux mois, dans les 12 mois précédents, et un engagement
                                            à poursuivre cette pratique selon la même périodicité
                                        </span>
                                    </div>
                                </Col>
                            </Row>

                            <Row gutters>
                                <Col lg={12}>
                                    <div className="margin-bottom-md">
                                        <InputFiles
                                            label="Attestation de formation initiale"
                                            {...register(
                                                'pieceJointes.formationSecuriteTir.fichiers',
                                                {
                                                    required: 'Ce champ est obligatoire',
                                                },
                                            )}
                                            error={
                                                formState.errors?.pieceJointes?.formationSecuriteTir
                                            }
                                            required
                                        />
                                        <span className="fr-hint-text">
                                            Veuillez fournir une attestation de suivi d'une
                                            formation initiale aux règles de sécurité, stockage et
                                            manipulation et d'usage de cette arme au cours des douze
                                            mois précédant la demande
                                        </span>
                                    </div>
                                </Col>
                            </Row>

                            <Row gutters className="margin-top-md">
                                <Col lg={12}>
                                    <div className="margin-bottom-md">
                                        <InputFiles
                                            label="Autorisation de détention d'arme"
                                            name="pieceJointes.autorisationDetentionArme.fichiers"
                                            error={
                                                formState.errors?.pieceJointes
                                                    ?.autorisationDetentionArme
                                            }
                                            required
                                        />
                                        <span className="fr-hint-text">
                                            Veuillez importer votre autorisation de détention d'arme
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* Cas 2: Personnes exposées à des risques professionnels */}
                    {isCasRisquesProfessionnels && (
                        <>
                            <Row gutters>
                                <Col lg={12}>
                                    <div className="margin-bottom-md">
                                        <InputFiles
                                            label="Fiche descriptive des armes"
                                            {...register(
                                                'pieceJointes.ficheCaracteristiquesArmes.fichiers',
                                                {
                                                    required: 'Ce champ est obligatoire',
                                                },
                                            )}
                                            error={
                                                formState.errors?.pieceJointes
                                                    ?.ficheCaracteristiquesArmes
                                            }
                                            required
                                        />
                                        <span className="fr-hint-text">
                                            Veuillez fournir une fiche donnant les caractéristiques
                                            des armes conformes au modèle fixé par l'arrêté prévu à
                                            l'article R.311-6 et mentionnant les dates d'acquisition
                                            des armes
                                        </span>
                                    </div>
                                </Col>
                            </Row>

                            {/* Certificat de résidence - obligatoire pour les personnes majeures non françaises */}
                            {isMajeure && codePaysNaissance !== 'FR' && (
                                <>
                                    <Row gutters>
                                        <Col lg={12}>
                                            <div className="margin-bottom-md">
                                                <InputFiles
                                                    label="Certificat de résidence"
                                                    {...register(
                                                        'pieceJointes.certificatDeResidence.fichiers',
                                                        {
                                                            required:
                                                                'Ce champ est obligatoire pour les personnes majeures non françaises',
                                                        },
                                                    )}
                                                    error={
                                                        formState.errors?.pieceJointes
                                                            ?.certificatDeResidence
                                                    }
                                                    required
                                                />
                                                <span className="fr-hint-text">
                                                    Pour les personnes majeures ne possédant pas la
                                                    nationalité française, veuillez fournir un
                                                    certificat de résidence ou tout document
                                                    équivalent
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row gutters>
                                        <Col lg={12}>
                                            <div className="margin-bottom-md">
                                                <div>
                                                    <FieldsetControl
                                                        id="adress-fieldset"
                                                        state={
                                                            formState.errors?.selectAdress
                                                                ? 'error'
                                                                : null
                                                        }
                                                        message={
                                                            formState.errors?.selectAdress?.message
                                                        }
                                                    >
                                                        <RadioControl
                                                            id="selectAdress-isAdressLocalPro"
                                                            label="Je souhaite renseigner l’adresse du local professionnel où sera stockée la seconde arme"
                                                            value="isAdressLocalPro"
                                                            aria-label="adresse du local professionnel"
                                                            {...register('selectAdress', {
                                                                required:
                                                                    'Ce champ est obligatoire',
                                                            })}
                                                            onClick={() =>
                                                                setAfficherAddressSecondaire(true)
                                                            }
                                                        />
                                                        <RadioControl
                                                            id="selectAdress-isAdressSecondaire"
                                                            label="Je souhaite renseigner l’adresse de la résidence secondaire où sera stockée la seconde arme"
                                                            aria-label="adresse de la résidence secondaire"
                                                            value="isAdressSecondaire"
                                                            {...register('selectAdress', {
                                                                required:
                                                                    'Ce champ est obligatoire',
                                                            })}
                                                            onClick={() =>
                                                                setAfficherAddressSecondaire(true)
                                                            }
                                                        />
                                                    </FieldsetControl>
                                                </div>

                                                {afficherAddressSecondaire && (
                                                    <CreerCompteAdresse
                                                        title={titreAdress[watch('selectAdress')]}
                                                        callApiAdressePrincipale={false}
                                                        renderFormProvider={false}
                                                        afficherInformations={false}
                                                        afficherJustificatifDomicile={false}
                                                        afficherCertificatMedical={false}
                                                        afficherItenerant={false}
                                                        desactiverChamps
                                                        uuidBrouillon={personnePhysiqueUuid}
                                                        isAncienLicencie={false}
                                                        setSuccessSubmit={
                                                            setIsSuccessSubmitAdresseSecondaire
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row gutters>
                                        <Col lg={12}>
                                            <div className="margin-bottom-md">
                                                <InputFiles
                                                    label="Attestation de formation aux règles de sécurité"
                                                    {...register(
                                                        'pieceJointes.attestationSuiviFormationTir.fichiers',
                                                        {
                                                            required: 'Ce champ est obligatoire',
                                                        },
                                                    )}
                                                    error={
                                                        formState.errors?.pieceJointes
                                                            ?.attestationSuiviFormationTir
                                                    }
                                                    required
                                                    texteAide="Veuillez fournir une attestation du suivi de la formation initiale aux règles de sécurité, de stockage et de manipulation des armes"
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </>
                    )}

                    {/* Boutons de navigation */}
                    <Row gutters className="margin-top-md">
                        <Col lg={6}>
                            <Button
                                name="etapePrecedente"
                                variant="secondary"
                                onClick={() => dispatch(etapePrecedente())}
                            >
                                Retour
                            </Button>
                        </Col>
                        <Col lg={6}>
                            <div className="text-right">
                                <Button
                                    name="deposer-demande"
                                    variant="primary"
                                    type="submit"
                                    isLoading={loading}
                                    disabled={!isValid || loading}
                                >
                                    Déposer la demande
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </form>
            </FormProvider>
        </SegmentLoader>
    );
};

CreerTitreDetentionPersonnePhysiqueEtape2.propTypes = {
    defaultValues: PropTypes.object,
    loading: PropTypes.bool,
    enregistrer: PropTypes.func,
    type: PropTypes.string.isRequired,
};

export default CreerTitreDetentionPersonnePhysiqueEtape2;
